import { useState } from "react";
import { ArrowUpRight } from "lucide-react";
import { PROJECTS } from "@/data/content";
import { useReveal } from "@/hooks/useReveal";

export function Projects() {
  const head = useReveal();
  const [idx, setIdx] = useState(0);
  const p = PROJECTS[idx];

  return (
    <section id="projets" data-testid="projects-section" className="relative py-20 md:py-28 bg-ink overflow-hidden">
      <div className="absolute -left-40 top-1/3 w-[520px] h-[520px] rounded-full bg-mustard/10 blur-[140px] pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-8">
        <div ref={head.ref} className={head.className}>
          <p className="font-mono2 text-xs uppercase tracking-[0.3em] text-emerald-bright mb-3">Action culturelle</p>
          <h2 className="font-display font-bold text-3xl md:text-5xl tracking-tight max-w-3xl">
            Projets <span className="text-mustard">artistiques</span>
          </h2>
          <p className="mt-5 max-w-2xl text-cream/70 leading-relaxed">
            Le Hip-Hop comme outil de médiation : santé, justice, politique de la ville.
            Des projets menés au quotidien avec les institutions, les quartiers et leurs habitants.
          </p>
        </div>

        <div className="grid lg:grid-cols-12 gap-6 lg:gap-10 mt-12">
          <ol className="lg:col-span-5 flex flex-col divide-y divide-white/5 border-y border-white/5" data-testid="projects-list">
            {PROJECTS.map((pr, i) => {
              const on = i === idx;
              return (
                <li key={pr.key}>
                  <button data-testid={`project-tab-${pr.key}`} onClick={() => setIdx(i)}
                    className={`group w-full text-left py-5 flex items-start gap-4 transition-colors ${on ? "text-cream" : "text-cream/50 hover:text-cream/80"}`}>
                    <span className={`font-mono2 text-xs mt-1.5 w-7 shrink-0 transition-colors ${on ? "text-mustard" : "text-cream/30"}`}>0{i + 1}</span>
                    <span className="flex-1">
                      <span className="block font-display font-bold text-lg md:text-xl tracking-tight">{pr.name}</span>
                      <span className="block mt-1 font-mono2 text-[11px] uppercase tracking-wider text-cream/40">{pr.theme}</span>
                    </span>
                    <span className={`mt-2 h-[2px] rounded-full bg-mustard transition-all duration-500 ${on ? "w-10" : "w-0 group-hover:w-4"}`} />
                  </button>
                </li>
              );
            })}
          </ol>

          <article key={p.key} data-testid="project-detail" className="lg:col-span-7 glass rounded-3xl overflow-hidden border border-white/5 animate-fade-up">
            <div className="relative aspect-[16/9] overflow-hidden bg-ink-2">
              <img src={p.img} alt={p.name} data-testid="project-image"
                className="w-full h-full object-cover saturate-[0.55] contrast-[1.08] brightness-[0.85]" />
              <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/30 to-transparent" />
              <div className="absolute inset-0 bg-mustard/15 mix-blend-multiply" />
              <div className="absolute left-6 bottom-5 flex flex-wrap items-center gap-2">
                <span className="tag-emerald rounded-md px-2 py-0.5 text-[10px] font-mono2 uppercase tracking-wider">{p.theme}</span>
                <span className="rounded-md px-2 py-0.5 text-[10px] font-mono2 uppercase tracking-wider border border-mustard/40 text-mustard">{p.sub}</span>
              </div>
            </div>
            <div className="p-6 md:p-8">
              <h3 className="font-display font-black text-2xl md:text-3xl tracking-tight text-cream" data-testid="project-title">{p.name}</h3>
              <p className="mt-3 text-mustard font-sub font-semibold">{p.intro}</p>
              <p className="mt-4 text-cream/70 leading-relaxed text-sm md:text-base">{p.text}</p>
              <div className="mt-6 flex flex-wrap items-center gap-2">
                <span className="font-mono2 text-[11px] uppercase tracking-wider text-cream/40 mr-1">Partenaires</span>
                {p.partners.map((pa) => (
                  <span key={pa} className="rounded-full px-3 py-1 text-xs font-mono2 border border-white/10 text-cream/70">{pa}</span>
                ))}
              </div>
              <a href="#contact" data-testid="project-contact-link"
                onClick={(e) => { e.preventDefault(); document.querySelector('[data-testid="newsletter-section"]')?.scrollIntoView({ behavior: "smooth" }); }}
                className="mt-7 inline-flex items-center gap-2 text-sm font-sub font-semibold text-cream link-underline">
                Monter un projet avec nous <ArrowUpRight className="w-4 h-4 text-mustard" />
              </a>
            </div>
          </article>
        </div>
      </div>
    </section>
  );
}
